/**
 * Ace Renderer Component
 * 
 * This component can be used anywhere in the Ace-ShadCN application where
 * content with mathematical notation needs to be displayed:
 * - Chat messages in beacon chat
 * - Dashboard content
 * - Educational content
 * - User-generated content
 * - API responses containing mathematical expressions
 * 
 * It automatically handles LaTeX rendering and Markdown formatting
 * as the content appears in JSON database format.
 */

import React, { useMemo, useState } from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, AlertTriangle } from 'lucide-react';
import 'katex/dist/katex.min.css';
import { 
  processContent, 
  processInlineContent, 
  processMarkdownText, 
  analyzeContent,
  type ProcessedContent,
  type ProcessedContentBlock
} from '@/lib/content-processor';

export interface AceRendererProps {
  /** Raw content from database/JSON - can contain LaTeX and Markdown */
  content: string;
  /** Optional CSS class for styling */
  className?: string;
  /** Show debug information and raw content toggle */
  showDebug?: boolean;
  /** Enable/disable LaTeX rendering (fallback to raw text) */
  enableMath?: boolean;
  /** Custom error handling for LaTeX rendering errors */
  onMathError?: (error: Error, expression: string) => void;
  /** Show content analysis badge */
  showAnalysis?: boolean;
  /** Compact mode for chat messages */
  compact?: boolean;
}

/**
 * Renders a single inline content block (text, inline math, or block math)
 */
const InlineContentBlock: React.FC<{
  block: ProcessedContentBlock;
  enableMath: boolean;
  onMathError?: (error: Error, expression: string) => void;
}> = ({ block, enableMath, onMathError }) => {
  if (block.type === 'math-inline' && enableMath) {
    try {
      return <InlineMath key={block.key} math={block.content} />;
    } catch (error) {
      onMathError?.(error as Error, block.content);
      return (
        <code className="bg-red-100 text-red-800 px-1 py-0.5 rounded text-sm">
          ${block.content}$
        </code>
      );
    }
  }

  if (block.type === 'math-block' && enableMath) {
    try {
      return (
        <div className="my-4 flex justify-center">
          <BlockMath key={block.key} math={block.content} />
        </div>
      );
    } catch (error) {
      onMathError?.(error as Error, block.content);
      return (
        <code className="block bg-red-100 text-red-800 p-2 rounded my-2 text-sm">
          $${block.content}$$
        </code>
      );
    }
  }

  // Regular text with Markdown formatting
  const formattedText = processMarkdownText(block.content);
  
  return (
    <span 
      key={block.key}
      dangerouslySetInnerHTML={{ __html: formattedText }}
    />
  );
};

/**
 * Renders a processed content block (paragraph, list, etc.)
 */
const ContentBlock: React.FC<{
  block: ProcessedContent;
  enableMath: boolean;
  onMathError?: (error: Error, expression: string) => void;
  compact?: boolean;
}> = ({ block, enableMath, onMathError, compact }) => {
  const baseSpacing = compact ? 'mb-2' : 'mb-4';

  switch (block.type) {
    case 'list-ordered':
      return (
        <ol 
          key={block.key} 
          className={`list-decimal list-inside space-y-1 ${baseSpacing} ${compact ? 'text-sm' : ''}`}
        >
          {Array.isArray(block.content) && block.content.map((item) => (
            <li key={item.key} className="leading-relaxed">
              {processInlineContent(item.content).map(inlineBlock => (
                <InlineContentBlock
                  key={inlineBlock.key}
                  block={inlineBlock}
                  enableMath={enableMath}
                  onMathError={onMathError}
                />
              ))}
            </li>
          ))}
        </ol>
      );

    case 'list-unordered':
      return (
        <ul 
          key={block.key} 
          className={`list-disc list-inside space-y-1 ${baseSpacing} ${compact ? 'text-sm' : ''}`}
        >
          {Array.isArray(block.content) && block.content.map((item) => (
            <li key={item.key} className="leading-relaxed">
              {processInlineContent(item.content).map(inlineBlock => (
                <InlineContentBlock
                  key={inlineBlock.key}
                  block={inlineBlock}
                  enableMath={enableMath}
                  onMathError={onMathError}
                />
              ))}
            </li>
          ))}
        </ul>
      );

    case 'table':
      const tableData = block.content as import('@/lib/content-processor').TableData;
      return (
        <div key={block.key} className={`overflow-x-auto ${baseSpacing}`}>
          <table className={`border-collapse border border-gray-300 w-full ${compact ? 'text-sm' : ''}`}>
            {tableData.headers && tableData.headers.length > 0 && (
              <thead>
                <tr className="bg-gray-50">
                  {tableData.headers.map((header, index) => (
                    <th 
                      key={`header-${index}`}
                      className={`border border-gray-300 px-3 py-2 font-semibold text-left ${
                        tableData.alignment?.[index] === 'center' ? 'text-center' :
                        tableData.alignment?.[index] === 'right' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {processInlineContent(header).map(inlineBlock => (
                        <InlineContentBlock
                          key={inlineBlock.key}
                          block={inlineBlock}
                          enableMath={enableMath}
                          onMathError={onMathError}
                        />
                      ))}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            {tableData.rows && tableData.rows.length > 0 && (
              <tbody>
                {tableData.rows.map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}`} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-25'}>
                    {row.map((cell, cellIndex) => (
                      <td 
                        key={`cell-${rowIndex}-${cellIndex}`}
                        className={`border border-gray-300 px-3 py-2 ${
                          tableData.alignment?.[cellIndex] === 'center' ? 'text-center' :
                          tableData.alignment?.[cellIndex] === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {processInlineContent(cell).map(inlineBlock => (
                          <InlineContentBlock
                            key={inlineBlock.key}
                            block={inlineBlock}
                            enableMath={enableMath}
                            onMathError={onMathError}
                          />
                        ))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      );

    case 'latex-env':
      const latexEnv = block.content as import('@/lib/content-processor').LatexEnvironment;
      
      // Render different LaTeX environments with appropriate styling
      const getEnvClassName = (envType: string) => {
        switch (envType) {
          case 'quote':
            return 'border-l-4 border-gray-300 pl-4 italic text-gray-700 bg-gray-50 p-3 rounded-r';
          case 'theorem':
            return 'border border-blue-200 bg-blue-50 p-3 rounded';
          case 'definition':
            return 'border border-green-200 bg-green-50 p-3 rounded';
          case 'proof':
            return 'border border-gray-200 bg-gray-50 p-3 rounded';
          default:
            return 'border border-gray-200 bg-gray-50 p-3 rounded';
        }
      };

      return (
        <div 
          key={block.key} 
          className={`${getEnvClassName(latexEnv.type)} ${baseSpacing} ${compact ? 'text-sm' : ''}`}
        >
          {latexEnv.content.map(inlineBlock => (
            <InlineContentBlock
              key={inlineBlock.key}
              block={inlineBlock}
              enableMath={enableMath}
              onMathError={onMathError}
            />
          ))}
        </div>
      );

    case 'paragraph':
    default:
      return (
        <div 
          key={block.key} 
          className={`leading-relaxed ${baseSpacing} ${compact ? 'text-sm' : ''}`}
        >
          {typeof block.content === 'string' && 
            processInlineContent(block.content).map(inlineBlock => (
              <InlineContentBlock
                key={inlineBlock.key}
                block={inlineBlock}
                enableMath={enableMath}
                onMathError={onMathError}
              />
            ))
          }
        </div>
      );
  }
};

/**
 * Main Ace Renderer Component
 */
export const AceRenderer: React.FC<AceRendererProps> = ({
  content,
  className = '',
  showDebug = false,
  enableMath = true,
  onMathError,
  showAnalysis = false,
  compact = false
}) => {
  const [showRaw, setShowRaw] = useState(false);
  const [mathErrors, setMathErrors] = useState<Array<{ error: Error; expression: string }>>([]);

  // Process content and analyze it
  const { processedBlocks, contentAnalysis } = useMemo(() => {
    if (!content || typeof content !== 'string') {
      return { processedBlocks: [], contentAnalysis: null };
    }

    const blocks = processContent(content);
    const analysis = showAnalysis ? analyzeContent(content) : null;
    
    return { processedBlocks: blocks, contentAnalysis: analysis };
  }, [content, showAnalysis]);

  // Handle math rendering errors
  const handleMathError = (error: Error, expression: string) => {
    setMathErrors(prev => [...prev, { error, expression }]);
    onMathError?.(error, expression);
  };

  // If no content, return nothing
  if (!content || typeof content !== 'string' || content.trim() === '') {
    return null;
  }

  return (
    <div className={`ace-renderer ${className}`}>
      {/* Debug Controls */}
      {showDebug && (
        <div className="mb-4 p-3 bg-slate-50 rounded-lg border">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-700">Ace Renderer Debug</h4>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRaw(!showRaw)}
                className="text-xs"
              >
                {showRaw ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                {showRaw ? 'Hide Raw' : 'Show Raw'}
              </Button>
            </div>
          </div>
          
          {contentAnalysis && (
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="secondary">
                {contentAnalysis.wordCount} words
              </Badge>
              <Badge variant="secondary">
                {contentAnalysis.mathExpressions.total} math expressions
              </Badge>
              {contentAnalysis.hasMarkdown && (
                <Badge variant="secondary">Markdown</Badge>
              )}
              {contentAnalysis.hasLists && (
                <Badge variant="secondary">Lists</Badge>
              )}
              <Badge variant={contentAnalysis.estimatedComplexity > 5 ? 'destructive' : 'default'}>
                Complexity: {contentAnalysis.estimatedComplexity}
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* Math Rendering Errors */}
      {mathErrors.length > 0 && (
        <Alert className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {mathErrors.length} LaTeX expression(s) failed to render. 
            They are displayed as raw text with red background.
          </AlertDescription>
        </Alert>
      )}

      {/* Raw Content View */}
      {showRaw && (
        <div className="mb-4 p-4 bg-slate-100 rounded-lg">
          <h4 className="text-sm font-medium text-slate-700 mb-2">Raw Content:</h4>
          <pre className="text-xs text-slate-600 whitespace-pre-wrap overflow-x-auto">
            {content}
          </pre>
        </div>
      )}

      {/* Processed Content */}
      <div className={`prose prose-slate max-w-none ${compact ? 'prose-sm' : ''}`}>
        {processedBlocks.map(block => (
          <ContentBlock
            key={block.key}
            block={block}
            enableMath={enableMath}
            onMathError={handleMathError}
            compact={compact}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Lightweight version for chat messages and inline content
 */
export const InlineAceRenderer: React.FC<{
  content: string;
  className?: string;
  enableMath?: boolean;
}> = ({ content, className = '', enableMath = true }) => {
  const inlineBlocks = useMemo(() => {
    return processInlineContent(content || '');
  }, [content]);

  if (!content || typeof content !== 'string') {
    return null;
  }

  return (
    <span className={className}>
      {inlineBlocks.map(block => (
        <InlineContentBlock
          key={block.key}
          block={block}
          enableMath={enableMath}
        />
      ))}
    </span>
  );
};

// Backward compatibility exports
export const ContentRenderer = AceRenderer;
export const InlineContentRenderer = InlineAceRenderer;

export default AceRenderer;
